/**
 * CustomWinderModal — create/edit form for a `winders/{docId}` catalog entry.
 *
 * Mirrors ElectronicModuleEditorModal's shape (lookup-backed pickers, alias
 * chips, AI-enrich panel feeding an apply-review diff), but writes the
 * shared-types `WinderEntry` via `saveWinder`.
 *
 * Two deliberate differences from the module editor:
 *
 *  - There is no manufacturer FK or quick-create. A winder's make is a
 *    `lookup_winder_manufacturers` SLUG, so the picker is an ordinary
 *    LookupFieldSelect and a new make is added through the Lookup Manager (or
 *    inline via the picker's own create-value path) — see the memo's Option A
 *    rationale: winder makes carry no queryable data of their own.
 *  - AI enrichment does not require a saved doc. `enrichWinder` takes make +
 *    model directly, so a brand-new winder can be enriched before its first save
 *    and the whole result applied in one pass.
 *
 * ⚠️ THIS EDITOR MUST BE OPENED ON A FIRESTORE DOCUMENT, never on a listing row.
 * `saveWinder` writes `null` for every optional field absent from its input, so
 * a `winder` prop that came from the Algolia projection deletes whatever the
 * index does not carry. `CuratedWinders.openEdit` hydrates before opening for
 * exactly that reason — see the ⚠️ note in `winderCatalogueQuery.ts` for the
 * thirty `imageStoragePath`s this cost.
 *
 * Form state and its three mappings live in `services/winderForm.ts`, so they
 * are testable without rendering this modal.
 */
import { useEffect, useMemo, useState } from 'react';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import {
  saveWinder,
  deleteWinder,
  deleteWinderImage,
  deriveWinderDocId,
  enrichWinderWithAI,
  getImageUrl,
  uploadWinderImage,
} from '../../services/firebase';
import type { WinderEntry } from '../../types';
import {
  APPLIANCE_KIND_LABELS,
  capabilitiesAreValid,
  resolveCapabilitiesWrite,
  type ApplianceCapabilities,
} from '../../services/winderApplianceKind';
import {
  WINDER_ALWAYS_APPLY_KEYS,
  coerceWinderValue,
  formatWinderValue,
  getWinderExisting,
  getWinderFieldLabel,
  getWinderSuggestion,
  suggestedWinderKeys,
} from '../../services/winderEnrichFields';
import {
  emptyForm,
  fromTri,
  numOrNull,
  winderApplyPatch,
  winderToForm,
  type TriState,
  type WinderFormState,
} from '../../services/winderForm';
import { normalizeTurnsPerDayRange } from '../../services/winderTurnsPerDay';
import {
  LOCKING_MECHANISMS,
  LOCKING_MECHANISM_LABELS,
} from '../../services/winderSafeSpecs';
import {
  GLAZING_LABELS,
  GLAZING_MATERIALS,
  MOUNTING_STYLES,
  MOUNTING_STYLE_LABELS,
} from '../../services/winderDisplayCaseSpecs';
import {
  WINDER_IMAGE_MIN_RECOMMENDED_WIDTH,
  formatDimensions,
  winderImageWarning,
  type ImageDimensions,
} from '../../services/winderImageSpec';
import { LookupFieldSelect } from '../CuratedWatches/LookupFieldSelect';
import { LookupMultiSelect } from '../CuratedWatches/LookupMultiSelect';
import { TagInput } from '../CuratedWatches/TagInput';
import { AiWinderEnrichPanel, type WinderAiEnrichState } from './AiWinderEnrichPanel';
import toast from 'react-hot-toast';

const inputClass =
  'w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500';

/**
 * The three capability checkboxes.
 *
 * The hints name the TEST rather than restating the label — "does it rotate
 * watches" is answerable about a product in a way "is it a winder" is not, and
 * the winder hint is where the ladder's tie-break actually lands for a curator.
 */
const CAPABILITY_BOXES: {
  key: 'capWinder' | 'capSafe' | 'capDisplayCase';
  label: string;
  hint: string;
}[] = [
  {
    key: 'capWinder',
    label: 'Is a winder',
    hint: 'It rotates watches — it has a rotor, even one built into a safe.',
  },
  {
    key: 'capSafe',
    label: 'Is a safe',
    hint: 'A hardened body built for security, not merely a locking case.',
  },
  {
    key: 'capDisplayCase',
    label: 'Is a display case',
    hint: 'A glazed surface the watches are meant to be viewed through.',
  },
];

/** A single field in the AI apply-review diff. */
interface AiFieldDiff {
  key: string;
  label: string;
  current: string;
  proposed: string;
  changed: boolean;
  apply: () => void;
}

interface CustomWinderModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Existing winder to edit; null = create new. */
  winder?: WinderEntry | null;
  /** Pre-fill the make when launched from a make-filtered list. */
  defaultMake?: string;
  /**
   * Called after a successful save so the parent can refresh. The editor does
   * NOT self-close on save — it stays open (transitioning a just-created winder
   * into an edit session), so callers should refresh here but must not force the
   * modal closed.
   */
  onSaved?: (savedId: string) => void;
  onDeleted?: (deletedId: string) => void;
}

export function CustomWinderModal({
  isOpen,
  onClose,
  winder,
  defaultMake,
  onSaved,
  onDeleted,
}: CustomWinderModalProps) {
  // Source of truth for "has this winder been persisted yet" — seeded from an
  // existing winder, then set on the first create-save, so the delete action and
  // the doc-id preview settle in place without waiting for a reopen.
  const [savedWinderId, setSavedWinderId] = useState<string | null>(winder?.id ?? null);
  const canDelete = !!winder;
  const isSavedView = !!winder || savedWinderId != null;
  const [form, setForm] = useState<WinderFormState>(() =>
    winder ? winderToForm(winder) : emptyForm(defaultMake),
  );
  const [saving, setSaving] = useState(false);

  // AI enrich
  const [uploadingImage, setUploadingImage] = useState(false);
  /**
   * Dimensions of the image AS PICKED, for the readout under the preview.
   *
   * Two sources, because there are two ways an image reaches this form:
   * `uploadWinderImage` reports them for one the curator just chose (before any
   * downscale, so the number matches the file on their disk), and the preview
   * `<img>` reports them on load for one already stored on the document.
   */
  const [imageDimensions, setImageDimensions] = useState<ImageDimensions | null>(
    null,
  );
  /** Preview URL for `form.imageStoragePath`, resolved from the path. */
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  /**
   * The object the form USED to point at, held until the save lands so it can
   * be binned then. Deleting on upload would strand the winder on missing bytes
   * if the admin closed the modal without saving.
   */
  const [supersededImagePath, setSupersededImagePath] = useState<string | null>(
    null,
  );
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [aiState, setAiState] = useState<WinderAiEnrichState>({});
  const [aiContext, setAiContext] = useState('');
  const [aiApplyOpen, setAiApplyOpen] = useState(false);
  const [aiFieldDiffs, setAiFieldDiffs] = useState<AiFieldDiff[]>([]);
  const [aiFieldSel, setAiFieldSel] = useState<Record<string, boolean>>({});

  // Reset the form whenever the modal opens for a different subject.
  useEffect(() => {
    if (!isOpen) return;
    setForm(winder ? winderToForm(winder) : emptyForm(defaultMake));
    setSavedWinderId(winder?.id ?? null);
    setShowAiPanel(false);
    setAiState({});
    setAiContext('');
    setAiApplyOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, winder?.id]);

  const updateForm = (patch: Partial<WinderFormState>) =>
    setForm((prev) => ({ ...prev, ...patch }));

  // The doc id this winder will get. Shown on create so the admin sees the slug
  // before committing to it; frozen to the saved id on edit, because a rename
  // must never re-key the doc (UserWinder.winderCatalogId FKs point at it).
  const previewDocId = useMemo(
    () => savedWinderId || deriveWinderDocId(form.make, form.model),
    [savedWinderId, form.make, form.model],
  );

  const identityReady = form.make.trim() !== '' && form.model.trim() !== '';

  /**
   * WHAT THIS APPLIANCE IS — three independent capabilities, not one choice.
   *
   * ⚠️ CHECKBOXES REPLACED A SEGMENTED CONTROL, and the change is more than
   * cosmetic. An appliance is frequently more than one of these at once: a
   * Buben & Zörweg Collector winds, protects AND displays. A radio group asked
   * the curator to pick a side about an object that does not have one, and then
   * to remember to tick the "and also" flags separately — two questions where
   * there is one.
   *
   * The primary `applianceKind` is DERIVED from these at save time by the
   * ladder in `resolveCapabilitiesWrite`, so nobody has to think about which
   * rung fires. The form holds the boxes; the write path holds the ladder.
   *
   * Read straight off the form rather than re-resolved: `winderToForm` already
   * ran `capabilitiesOf` on the way in, so a legacy entry with no fields at all
   * arrives with exactly "is a winder" ticked — and once the curator touches a
   * box their answer is the only one that matters.
   */
  const caps: ApplianceCapabilities = {
    winder: form.capWinder,
    safe: form.capSafe,
    displayCase: form.capDisplayCase,
  };
  const capsValid = capabilitiesAreValid(caps);
  /**
   * What to CALL this entry — the derived primary kind, run through the same
   * ladder the write path uses so the title and the document agree.
   */
  const primaryLabel =
    APPLIANCE_KIND_LABELS[resolveCapabilitiesWrite(caps).applianceKind ?? 'winder'];

  /**
   * Tick or untick one capability.
   *
   * Nothing is cleared here. Unticking hides a section but KEEPS what it holds,
   * so a mis-click is undoable and a curator who unticks "is a winder" to check
   * something does not lose the turns-per-day they typed. `saveWinder` is what
   * refuses to store a hidden section — the same division of labour
   * `resolveSafeSpecsWrite` and the TPD mode toggle already use.
   */
  const toggleCapability = (key: 'capWinder' | 'capSafe' | 'capDisplayCase') =>
    updateForm({ [key]: !form[key] } as Partial<typeof form>);

  // The two range inputs as the writer wants them — either bound may be blank
  // mid-edit, which is what makes this nullable rather than a stored range.
  const formRange = useMemo(
    () => ({
      min: form.turnsPerDayMin.trim() ? Number(form.turnsPerDayMin) : null,
      max: form.turnsPerDayMax.trim() ? Number(form.turnsPerDayMax) : null,
    }),
    [form.turnsPerDayMin, form.turnsPerDayMax],
  );

  // ---- AI enrich --------------------------------------------------------------
  const runAiEnrich = async () => {
    if (!identityReady) return;
    setAiState({ loading: true });
    try {
      const result = await enrichWinderWithAI(form.make.trim(), form.model.trim(), {
        ...(savedWinderId ? { winderId: savedWinderId } : {}),
        ...(aiContext.trim() ? { context: aiContext.trim() } : {}),
      });
      setAiState({ result });
    } catch (err) {
      console.error('Winder AI enrichment failed:', err);
      setAiState({
        error: err instanceof Error ? err.message : 'AI enrichment failed',
      });
    }
  };

  /**
   * Build the apply-review diff.
   *
   * Compares against the FORM's current values (not the saved doc), so unsaved
   * edits are what the "current" column shows and what "changed" is measured
   * against — otherwise a field the admin just typed would read as unchanged.
   */
  const openAiApplyReview = () => {
    const result = aiState.result;
    if (!result) return;

    // A synthetic entry so the shared existing-value reader can be pointed at
    // form state. Only the registry's fields are read from it.
    const formAsEntry = {
      // Always present, so the diff's "current" column shows what the form
      // already says rather than reading blank and pre-ticking a change.
      // Derived, because the form holds boxes rather than a kind.
      applianceKind: resolveCapabilitiesWrite(caps).applianceKind ?? 'winder',
      displayName: form.displayName || undefined,
      countryOfManufacture: form.countryOfManufacture || undefined,
      slotCount: form.slotCount ? Number(form.slotCount) : undefined,
      turnsPerDay: form.turnsPerDay.map((n) => Number(n)).filter(Number.isFinite),
      rotationDirection: form.rotationDirection || undefined,
      rotationProgramSlugs: form.rotationProgramSlugs,
      powerSourceSlugs: form.powerSourceSlugs,
      materialSlugs: form.materialSlugs,
      isQuiet: fromTri(form.isQuiet) ?? undefined,
      hasLock: fromTri(form.hasLock) ?? undefined,
      hasDisplayCase: caps.displayCase || undefined,
      priceTierSlug: form.priceTierSlug || undefined,
      description: form.description || undefined,
      aliases: form.aliases,
      sourceUrls: form.sourceUrls,
      // The v1.97.0 fields belong here too, or the review's "current" column
      // reads blank for a field the form already holds — which also pre-ticks it
      // as changed, so a reviewer skimming the diff re-applies values they had
      // already accepted or typed.
      turnsPerDayRange: normalizeTurnsPerDayRange(formRange) ?? undefined,
      startDelayMaxHours: numOrNull(form.startDelayMaxHours) ?? undefined,
      isSafe: caps.safe,
      safeSpecs: {
        certification: form.safeCertification || null,
        fireRatingMinutes: numOrNull(form.safeFireRatingMinutes),
        fireExternalTempC: numOrNull(form.safeFireExternalTempC),
        fireMaxInternalTempC: numOrNull(form.safeFireMaxInternalTempC),
        weightKg: numOrNull(form.safeWeightKg),
        lockingMechanism: form.safeLockingMechanism || null,
        lockingBoltCount: numOrNull(form.safeLockingBoltCount),
        wallThicknessMm: numOrNull(form.safeWallThicknessMm),
        hasAnchorPoints: fromTri(form.safeHasAnchorPoints),
        insuranceCashRating: form.safeInsuranceCashRating || null,
      },
    } as unknown as WinderEntry;

    const diffs: AiFieldDiff[] = [];
    for (const key of suggestedWinderKeys(result)) {
      const coerced = coerceWinderValue(key, getWinderSuggestion(result, key));
      if (coerced === null || coerced === undefined) continue;
      const current = getWinderExisting(formAsEntry, key);
      const proposed = formatWinderValue(coerced);
      diffs.push({
        key,
        label: getWinderFieldLabel(key),
        current,
        proposed,
        changed: current !== proposed,
        apply: () => applyCoercedToForm(key, coerced),
      });
    }

    setAiFieldDiffs(diffs);
    // Pre-check what changed, plus the always-apply description regardless.
    setAiFieldSel(
      Object.fromEntries(
        diffs.map((d) => [d.key, d.changed || WINDER_ALWAYS_APPLY_KEYS.has(d.key)]),
      ),
    );
    setAiApplyOpen(true);
  };

  /**
   * Route one coerced suggestion into the matching form field.
   *
   * The mapping itself is `winderApplyPatch`, kept pure in `winderForm.ts` so
   * every key can be round-trip tested without rendering this modal — which is
   * what let twelve of them go unmapped and unnoticed. It THROWS on a key it has
   * no case for; `applyAiFields` below contains that per field.
   */
  const applyCoercedToForm = (key: string, value: unknown) =>
    setForm((prev) => ({ ...prev, ...winderApplyPatch(key, value, prev) }));

  const applyAiFields = (keys: Set<string>) => {
    // One unmapped key must not cost the reviewer the other twenty they just
    // accepted, so a throw from `applyCoercedToForm` is contained per field and
    // surfaced — rather than aborting the loop half-applied and silently.
    const failed: string[] = [];
    for (const d of aiFieldDiffs) {
      if (!keys.has(d.key)) continue;
      try {
        d.apply();
      } catch (err) {
        console.error('Failed to apply AI field:', d.key, err);
        failed.push(d.label || d.key);
      }
    }
    if (failed.length) {
      toast.error(
        `Could not apply: ${failed.join(', ')}. This is a bug — please report it.`,
      );
    }
    setAiApplyOpen(false);
    setShowAiPanel(false);
    setAiState({});
    setAiFieldDiffs([]);
    setAiFieldSel({});
    const applied = keys.size - failed.length;
    if (applied > 0) {
      toast.success(`Applied ${applied} AI field${applied === 1 ? '' : 's'}`);
    }
  };

  const aiSelectedCount = aiFieldDiffs.filter((d) => aiFieldSel[d.key]).length;

  // ---- Save / delete ----------------------------------------------------------
  // Resolve the stored path to a displayable URL. Storage paths are not
  // directly renderable — getDownloadURL signs them, the same round-trip the
  // calibre image list makes.
  useEffect(() => {
    let cancelled = false;
    const path = form.imageStoragePath;
    if (!path) {
      setImageUrl(null);
      setImageDimensions(null);
      return;
    }
    getImageUrl(path)
      .then((url) => {
        if (!cancelled) setImageUrl(url);
      })
      .catch(() => {
        if (!cancelled) setImageUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [form.imageStoragePath]);

  const handleImageChosen = async (file: File) => {
    // The doc id is derived from make + model, so there is nothing to key an
    // upload against until the winder exists. The input is disabled in that
    // state; this is the backstop.
    const docId = savedWinderId ?? winder?.id ?? null;
    if (!docId) {
      toast.error(`Save the ${primaryLabel.toLowerCase()} first, then add its image`);
      return;
    }
    setUploadingImage(true);
    try {
      const { storagePath: path, width, height } = await uploadWinderImage(
        docId,
        file,
      );
      setImageDimensions(width > 0 && height > 0 ? { width, height } : null);
      // Remember what we are replacing rather than deleting it now.
      setSupersededImagePath(
        (current) => current ?? (form.imageStoragePath || null),
      );
      updateForm({ imageStoragePath: path });
      toast.success('Image uploaded — press Save to publish it');
    } catch (err) {
      console.error('Failed to upload winder image:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleImageRemoved = () => {
    setSupersededImagePath(
      (current) => current ?? (form.imageStoragePath || null),
    );
    setImageDimensions(null);
    updateForm({ imageStoragePath: '' });
  };

  const handleSave = async () => {
    if (!form.make.trim()) {
      toast.error('Make is required');
      return;
    }
    if (!form.model.trim()) {
      toast.error('Model is required');
      return;
    }
    // Range mode with an unusable range is blocked rather than saved-as-empty:
    // the writer would clear both TPD fields, and an admin who mistyped a bound
    // should see why instead of finding the setting gone on the next open.
    if (form.turnsPerDayMode === 'range' && !normalizeTurnsPerDayRange(formRange)) {
      toast.error(
        'Enter a turns-per-day range as two positive numbers, lowest first — or untick “TPD is a range”.',
      );
      return;
    }
    const priorId = savedWinderId ?? winder?.id ?? null;
    const isFirstSave = !priorId;
    setSaving(true);
    try {
      const savedId = await saveWinder({
        id: priorId ?? undefined,
        /*
         * The capability boxes ARE the answer here, and `saveWinder` runs the
         * ladder on them rather than being handed a kind. This is the one
         * caller where the boxes must win over the stored fields: a curator who
         * unticks "is a winder" means it, and deriving from fields the form is
         * still holding would silently ignore them.
         */
        capabilities: caps,
        /*
         * Every gated field below is handed over UNCONDITIONALLY, whatever is
         * ticked. `saveWinder` nulls what does not apply — the same division of
         * labour the safeSpecs block already uses: the form states what it
         * holds, the write path enforces what may be stored. Filtering here as
         * well would put the invariant in two places that could disagree, and
         * the form deliberately keeps hidden values so a mis-tick is undoable.
         */
        make: form.make.trim(),
        model: form.model.trim(),
        displayName: form.displayName.trim() || undefined,
        countryOfManufacture: form.countryOfManufacture.trim() || undefined,
        slotCount: form.slotCount.trim() ? Number(form.slotCount) : null,
        // Both representations are handed over with the mode that decides
        // between them; `saveWinder` writes one and clears the other.
        turnsPerDayMode: form.turnsPerDayMode,
        turnsPerDay: form.turnsPerDay.map((n) => Number(n)),
        turnsPerDayRange: formRange,
        rotationDirection:
          (form.rotationDirection.trim() as WinderEntry['rotationDirection']) || null,
        rotationProgramSlugs: form.rotationProgramSlugs,
        powerSourceSlugs: form.powerSourceSlugs,
        materialSlugs: form.materialSlugs,
        isQuiet: fromTri(form.isQuiet),
        hasLock: fromTri(form.hasLock),
        hasDisplayCase: caps.displayCase,
        isSafe: caps.safe,
        startDelayMaxHours: form.startDelayMaxHours.trim()
          ? Number(form.startDelayMaxHours)
          : null,
        // Handed over whole; `saveWinder` nulls the block when the entry is
        // neither a safe appliance nor a winder-in-a-safe, and when every field
        // is empty, so the form does not have to.
        safeSpecs: {
          certification: form.safeCertification.trim() || null,
          fireRatingMinutes: numOrNull(form.safeFireRatingMinutes),
          fireExternalTempC: numOrNull(form.safeFireExternalTempC),
          fireMaxInternalTempC: numOrNull(form.safeFireMaxInternalTempC),
          weightKg: numOrNull(form.safeWeightKg),
          lockingMechanism:
            (form.safeLockingMechanism.trim() as NonNullable<
              WinderEntry['safeSpecs']
            >['lockingMechanism']) || null,
          lockingBoltCount: numOrNull(form.safeLockingBoltCount),
          wallThicknessMm: numOrNull(form.safeWallThicknessMm),
          hasAnchorPoints: fromTri(form.safeHasAnchorPoints),
          insuranceCashRating: form.safeInsuranceCashRating.trim() || null,
        },

        // Handed over whole, like safeSpecs above;
        // `resolveDisplayCaseSpecsWrite` nulls the block when the entry is not
        // a display case and when every field is empty.
        displayCaseSpecs: {
          glazing:
            (form.displayGlazing.trim() as NonNullable<
              WinderEntry['displayCaseSpecs']
            >['glazing']) || null,
          hasUvFiltering: fromTri(form.displayHasUvFiltering),
          hasIllumination: fromTri(form.displayHasIllumination),
          hasHygrometer: fromTri(form.displayHasHygrometer),
          hasHumidityControl: fromTri(form.displayHasHumidityControl),
          mountingStyle:
            (form.displayMountingStyle.trim() as NonNullable<
              WinderEntry['displayCaseSpecs']
            >['mountingStyle']) || null,
          hasDrawer: fromTri(form.displayHasDrawer),
        },
        priceTierSlug: form.priceTierSlug.trim() || null,
        description: form.description.trim() || null,
        aliases: form.aliases,
        sourceUrls: form.sourceUrls,
        isDeprecated: form.isDeprecated,
        imageStoragePath: form.imageStoragePath.trim() || null,
      });
      toast.success(`${primaryLabel} ${isFirstSave ? 'created' : 'updated'}`);
      setSavedWinderId(savedId);
      // The object the document no longer points at, binned only now that the
      // write has landed — deleting on upload would strand the winder on
      // missing bytes if the admin then closed without saving.
      if (supersededImagePath && supersededImagePath !== form.imageStoragePath) {
        void deleteWinderImage(supersededImagePath);
        setSupersededImagePath(null);
      }
      onSaved?.(savedId);
    } catch (err) {
      console.error('Failed to save winder:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to save winder');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!winder) return;
    if (
      !window.confirm(
        `Delete ${primaryLabel.toLowerCase()} "${
          winder.displayName || `${winder.make} ${winder.model}`
        }"? This cannot be undone, and any user who has added it keeps a UserWinder pointing at a missing catalog entry. Prefer "Deprecated" if it is simply discontinued.`,
      )
    )
      return;
    setSaving(true);
    try {
      await deleteWinder(winder.id);
      toast.success(`${primaryLabel} deleted`);
      onDeleted?.(winder.id);
      onClose();
    } catch (err) {
      console.error('Failed to delete appliance:', err);
      toast.error(`Failed to delete ${primaryLabel.toLowerCase()}`);
    } finally {
      setSaving(false);
    }
  };

  const triSelect = (
    label: string,
    value: TriState,
    onChange: (v: TriState) => void,
    hint: string,
  ) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as TriState)}
        className={inputClass}
        title={hint}
      >
        <option value="">— Unknown —</option>
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </select>
    </div>
  );

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={
          isSavedView
            ? `Edit ${primaryLabel.toLowerCase()} — ${
                winder?.displayName || `${form.make} ${form.model}`.trim()
              }`
            : `New ${primaryLabel.toLowerCase()}`
        }
        size="xl"
      >
        <div className="space-y-5">
          {/* What it is — the question every other answer hangs off, so it sits
              above Identification rather than inside it. */}
          <section className="space-y-2">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              What is it?
            </h4>
            <div
              className="grid grid-cols-1 sm:grid-cols-3 gap-2"
              role="group"
              aria-label="Appliance capabilities"
            >
              {CAPABILITY_BOXES.map(({ key, label, hint }) => {
                const on = form[key];
                return (
                  <label
                    key={key}
                    className={`flex flex-col gap-1 rounded-lg border-l-4 border px-3 py-2 cursor-pointer transition-colors ${
                      on
                        ? 'border-brand-500 border-l-brand-500 bg-brand-50'
                        : 'border-gray-300 border-l-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => toggleCapability(key)}
                        /* Explicit, because the wrapping label also carries the
                           hint line — without this the accessible name is the
                           question and its explanation run together. */
                        aria-label={label}
                        className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                      />
                      <span className="text-sm font-semibold text-gray-800">
                        {label}
                      </span>
                    </span>
                    <span className="pl-6 text-xs text-gray-500">{hint}</span>
                  </label>
                );
              })}
            </div>
            {capsValid ? (
              <p className="text-xs text-gray-500">
                Filed as a <strong>{primaryLabel.toLowerCase()}</strong>.{' '}
                <span className="text-gray-400">
                  Anything with a rotor is a winder, whatever else it does — so
                  a Buben &amp; Zörweg Collector is a winder that is also a safe
                  and also a display case, and appears under all three. Unticked
                  sections keep what they hold, but saving clears them from the
                  document.
                </span>
              </p>
            ) : (
              <p className="text-sm text-red-600">
                Tick at least one — an entry that is none of these is not an
                appliance.
              </p>
            )}
          </section>

          {/* Identification */}
          <section className="space-y-3">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Identification
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <LookupFieldSelect
                label="Make *"
                value={form.make}
                lookupCollection="lookup_winder_manufacturers"
                onChange={(v) => updateForm({ make: v })}
                placeholder="e.g. Wolf, Barrington"
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Model *
                </label>
                <input
                  type="text"
                  value={form.model}
                  onChange={(e) => updateForm({ model: e.target.value })}
                  placeholder="e.g. Cub Single Winder"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Display name{' '}
                  <span className="font-normal text-gray-400">
                    (defaults to “make model”)
                  </span>
                </label>
                <input
                  type="text"
                  value={form.displayName}
                  onChange={(e) => updateForm({ displayName: e.target.value })}
                  placeholder="e.g. Wolf Cub Single Winder"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Country of manufacture
                </label>
                <input
                  type="text"
                  value={form.countryOfManufacture}
                  onChange={(e) =>
                    updateForm({ countryOfManufacture: e.target.value })
                  }
                  placeholder="e.g. Germany"
                  className={inputClass}
                />
              </div>
            </div>
            <TagInput
              label="Also known as"
              values={form.aliases}
              onChange={(v) => updateForm({ aliases: v })}
              placeholder="Add an alternative model name…"
            />
            <p className="text-xs text-gray-400">
              Document id:{' '}
              <code className="bg-gray-100 px-1.5 py-0.5 rounded">
                {previewDocId || '—'}
              </code>
              {savedWinderId
                ? ' · fixed for this winder — renaming the make or model will not re-key it.'
                : ' · derived from make + model, and fixed once created.'}
            </p>
          </section>

          {/* Catalog image */}
          <section className="space-y-3">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Catalog image
            </h4>
            <div className="flex items-start gap-4">
              <div className="w-40 shrink-0">
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={`${form.make} ${form.model}`.trim() || 'Winder'}
                    // Square and `object-contain`, matching how the RN hero
                    // draws it — a cropped preview here would hide exactly the
                    // letterboxing the warning below is about to mention.
                    className="w-40 aspect-square object-contain rounded-lg border border-gray-200 bg-gray-50"
                    onLoad={(e) => {
                      // Only for an image already on the document; a freshly
                      // uploaded one already reported its pre-downscale size,
                      // which is the more truthful number to show.
                      const el = e.currentTarget;
                      setImageDimensions((current) =>
                        current ??
                        (el.naturalWidth && el.naturalHeight
                          ? { width: el.naturalWidth, height: el.naturalHeight }
                          : null),
                      );
                    }}
                  />
                ) : (
                  <div className="w-40 aspect-square rounded-lg border border-dashed border-gray-300 bg-gray-50 flex items-center justify-center text-xs text-gray-400">
                    {form.imageStoragePath ? 'Loading…' : 'No image'}
                  </div>
                )}
              </div>

              <div className="flex-1 space-y-2">
                {/* Disabled until the entry exists: the Storage path is keyed
                    by the document id, which is derived on first save. */}
                {!isSavedView && (
                  <p className="text-xs text-amber-600">
                    Save the {primaryLabel.toLowerCase()} first — the image is
                    stored under its document id.
                  </p>
                )}
                <div className="flex items-center gap-2">
                  <label
                    className={`inline-flex items-center px-3 py-2 rounded-lg border text-sm font-medium ${
                      isSavedView && !uploadingImage
                        ? 'border-gray-300 text-gray-700 hover:bg-gray-50 cursor-pointer'
                        : 'border-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    {uploadingImage
                      ? 'Uploading…'
                      : form.imageStoragePath
                        ? 'Replace image'
                        : 'Choose file'}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={!isSavedView || uploadingImage}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        // Reset the input so choosing the SAME file twice after
                        // a failed upload still fires a change event.
                        e.target.value = '';
                        if (file) void handleImageChosen(file);
                      }}
                    />
                  </label>

                  {form.imageStoragePath && (
                    <button
                      type="button"
                      onClick={handleImageRemoved}
                      disabled={uploadingImage}
                      className="px-3 py-2 rounded-lg border border-gray-300 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                    >
                      Remove
                    </button>
                  )}
                </div>

                {/* Readout + advisory warning, the shape BrandBannerFallbacks
                    uses. Nothing here rejects a file: a curator cannot fix a
                    rejected image from inside this tool, and an off-spec image
                    still renders and still beats the generic glyph. */}
                {imageDimensions && (
                  <p className="text-xs text-gray-500">
                    Source: {formatDimensions(imageDimensions)}
                  </p>
                )}
                {imageDimensions && winderImageWarning(imageDimensions) && (
                  <p className="text-xs text-amber-600">
                    {winderImageWarning(imageDimensions)}
                  </p>
                )}

                <p className="text-xs text-gray-500">
                  Shown in the app when a winder&rsquo;s owner has not added
                  their own photo. Recommended: 1:1 square, at least{' '}
                  {WINDER_IMAGE_MIN_RECOMMENDED_WIDTH}px, JPEG/PNG/WebP. Other
                  shapes are letterboxed, never cropped; larger images are
                  resized to 1600px on upload.
                </p>
                {form.imageStoragePath && (
                  <p className="text-xs text-gray-400">
                    <code className="bg-gray-100 px-1.5 py-0.5 rounded break-all">
                      {form.imageStoragePath}
                    </code>
                  </p>
                )}
              </div>
            </div>
          </section>

          {/* Capacity & motion — "Capacity" alone when it does not wind, which
              has the one without the other. The winding fields are hidden
              rather than disabled: a greyed row of inputs a curator can never
              fill is noise on every safe and every display case in the
              catalogue. Their VALUES survive in form state so re-ticking "is a
              winder" restores them, and `saveWinder` clears them on the
              document either way.

              ⚠️ CAPACITY ITSELF IS NEVER HIDDEN. All three kinds publish one —
              a Wolf Athos 20 Piece holds 20, a Wolf Windsor 10 Piece holds 10 —
              and hiding it behind the winder box would blank a figure printed
              on the box, which is the bug watchlock #1032 fixed. */}
          <section className="space-y-3">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              {caps.winder ? 'Capacity & motion' : 'Capacity'}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {caps.winder && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start delay (hours){' '}
                    <span className="font-normal text-gray-400">
                      max programmable
                    </span>
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={form.startDelayMaxHours}
                    onChange={(e) =>
                      updateForm({ startDelayMaxHours: e.target.value })
                    }
                    placeholder="e.g. 255"
                    className={inputClass}
                  />
                </div>
              )}
              <div>
                {/* Renamed rather than hidden: capacity is a figure safes and
                    display cases publish and buyers compare — the Wolf Athos
                    "20 Piece" and the Windsor "10 Piece" are the number in the
                    model name. It is only the word "slots", with its
                    implication of individual rotors, that does not carry
                    across. */}
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {caps.winder ? 'Slots' : 'Watch capacity'}
                </label>
                <input
                  type="number"
                  min={1}
                  value={form.slotCount}
                  onChange={(e) => updateForm({ slotCount: e.target.value })}
                  placeholder={caps.winder ? 'e.g. 4' : 'e.g. 20'}
                  className={inputClass}
                />
              </div>
              {caps.winder && (
                <LookupFieldSelect
                  label="Rotation direction"
                  value={form.rotationDirection}
                  lookupCollection="lookup_winder_rotation_directions"
                  onChange={(v) => updateForm({ rotationDirection: v })}
                />
              )}
            </div>
            {/* TPD: one representation or the other, never both. The hidden
                side keeps its values so a mis-tick is undoable, but only the
                ticked side is saved — see `resolveTurnsPerDayWrite`. */}
            {caps.winder && (
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.turnsPerDayMode === 'range'}
                  onChange={(e) =>
                    updateForm({ turnsPerDayMode: e.target.checked ? 'range' : 'list' })
                  }
                  className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                />
                <span>
                  TPD is a range{' '}
                  <span className="text-gray-400">
                    — for a programmable winder set anywhere between two bounds
                  </span>
                </span>
              </label>
              {form.turnsPerDayMode === 'range' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Turns per day — minimum
                    </label>
                    <input
                      type="number"
                      min={1}
                      value={form.turnsPerDayMin}
                      onChange={(e) => updateForm({ turnsPerDayMin: e.target.value })}
                      placeholder="e.g. 300"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Turns per day — maximum
                    </label>
                    <input
                      type="number"
                      min={1}
                      value={form.turnsPerDayMax}
                      onChange={(e) => updateForm({ turnsPerDayMax: e.target.value })}
                      placeholder="e.g. 1200"
                      className={inputClass}
                    />
                  </div>
                </div>
              ) : (
                <TagInput
                  label="Turns per day (one per setting)"
                  values={form.turnsPerDay}
                  onChange={(v) =>
                    // Digits only, so a stray "650 TPD" can't become a NaN on save.
                    updateForm({ turnsPerDay: v.filter((n) => /^\d+$/.test(n.trim())) })
                  }
                  placeholder="e.g. 650"
                  transform={(v) => v.replace(/[^\d]/g, '')}
                />
              )}
              <p className="text-xs text-gray-400">
                Saving writes whichever of the two is showing and clears the other —
                a winder has a list of settings or a continuous range, not both.
              </p>
            </div>
            )}
            {caps.winder && (
              <LookupMultiSelect
                label="Programs"
                values={form.rotationProgramSlugs}
                lookupCollection="lookup_winder_programs"
                onChange={(v) => updateForm({ rotationProgramSlugs: v })}
              />
            )}
            {!caps.winder && (
              <p className="text-xs text-gray-400">
                Turns per day, rotation direction, programs and start delay are a
                winder&rsquo;s fields. This entry has none of them, and saving
                clears any that were set while &ldquo;is a winder&rdquo; was
                ticked.
              </p>
            )}
          </section>

          {/* Build */}
          <section className="space-y-3">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Build
            </h4>
            <LookupMultiSelect
              label="Power sources"
              values={form.powerSourceSlugs}
              lookupCollection="lookup_winder_power_sources"
              onChange={(v) => updateForm({ powerSourceSlugs: v })}
            />
            <LookupMultiSelect
              label="Materials"
              values={form.materialSlugs}
              lookupCollection="lookup_winder_materials"
              onChange={(v) => updateForm({ materialSlugs: v })}
            />
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              {triSelect(
                'Quiet operation',
                form.isQuiet,
                (v) => updateForm({ isQuiet: v }),
                'Only “Yes” when the manufacturer claims quiet/silent operation.',
              )}
              {triSelect('Lockable', form.hasLock, (v) => updateForm({ hasLock: v }), 'Key or combination lock on the case.')}
              <LookupFieldSelect
                label="Price tier"
                value={form.priceTierSlug}
                lookupCollection="lookup_winder_price_tiers"
                onChange={(v) => updateForm({ priceTierSlug: v })}
              />
            </div>
            <p className="text-xs text-gray-400">
              “Unknown” is a real answer for the two yes/no fields — leaving one
              unset marks it as a gap for a fill-empty bulk enrich, while an
              explicit “No” is treated as a fact and left alone.
            </p>
            {/* ⚠️ THE "IS A SAFE" CHECKBOX MOVED TO THE TOP OF THE FORM.

                It used to live here as "Winder is integrated into a safe",
                which is the same fact asked as a footnote to the winding
                section. It is now one of the three capability boxes, because
                that is what it always was: an independent statement about the
                object, not a modifier on the kind. This line is what remains,
                so a curator looking for it here is told where it went. */}
            {!caps.safe && (
              <p className="text-sm text-gray-600">
                Not a safe.{' '}
                <span className="text-gray-400">
                  Tick <strong>Is a safe</strong> at the top of this form to
                  record a hardened body built for security — e.g. Buben &amp;
                  Zörweg Object series — and to fill in the security specs. A
                  winder can be one too; the two are independent.
                </span>
              </p>
            )}

            {/* Security specs, shown for a safe and for a winder built into one.
                `saveWinder` clears the whole block when neither holds, so hiding
                the panel is presentation — the data cannot be left stranded
                behind it. */}
            {caps.safe && (
              <div className="mt-3 space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                <p className="text-xs text-gray-500">
                  Only fill what you can verify — leave the rest blank. Blank
                  fields are stored as absent, not as zero.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Certification
                    </label>
                    <input
                      type="text"
                      value={form.safeCertification}
                      onChange={(e) =>
                        updateForm({ safeCertification: e.target.value })
                      }
                      placeholder="e.g. EN 1143-1 Grade I, UL RSC"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Insurance cash rating
                    </label>
                    <input
                      type="text"
                      value={form.safeInsuranceCashRating}
                      onChange={(e) =>
                        updateForm({ safeInsuranceCashRating: e.target.value })
                      }
                      placeholder="e.g. $5,000 UL, €65,000 EN"
                      className={inputClass}
                    />
                  </div>
                  {/* The three fire fields are ONE spec stated three ways, so
                      they are grouped and captioned rather than scattered
                      among the security inputs. "Fire max temp (°C)" on its
                      own was ambiguous between the two temperatures, and
                      against a value of 177 it reads as a feeble rating when
                      it is in fact the protected figure. */}
                  <div className="md:col-span-2 rounded-md border border-gray-200 bg-white p-3">
                    <p className="text-sm font-medium text-gray-700">Fire rating</p>
                    <p className="text-xs text-gray-500 mt-0.5 mb-2">
                      One class, three numbers — how long, how fierce a fire, and
                      how cool the inside stays. UL Class 350 is 60 minutes at
                      927°C with the interior never passing 177°C. Fill only what
                      the manufacturer publishes; never work one temperature out
                      from the other unless the class itself is stated.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Duration (minutes)
                        </label>
                        <input
                          type="number"
                          min={0}
                          value={form.safeFireRatingMinutes}
                          onChange={(e) =>
                            updateForm({ safeFireRatingMinutes: e.target.value })
                          }
                          placeholder="e.g. 60"
                          className={inputClass}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Fire exposure (°C)
                        </label>
                        <input
                          type="number"
                          min={0}
                          value={form.safeFireExternalTempC}
                          onChange={(e) =>
                            updateForm({ safeFireExternalTempC: e.target.value })
                          }
                          placeholder="e.g. 927"
                          className={inputClass}
                        />
                        <p className="text-[11px] text-gray-400 mt-1">
                          The furnace it is tested in.
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Interior max (°C)
                        </label>
                        <input
                          type="number"
                          min={0}
                          value={form.safeFireMaxInternalTempC}
                          onChange={(e) =>
                            updateForm({ safeFireMaxInternalTempC: e.target.value })
                          }
                          placeholder="e.g. 177"
                          className={inputClass}
                        />
                        <p className="text-[11px] text-gray-400 mt-1">
                          What the watches inside stay below.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Weight (kg)
                    </label>
                    <input
                      type="number"
                      min={0}
                      value={form.safeWeightKg}
                      onChange={(e) => updateForm({ safeWeightKg: e.target.value })}
                      placeholder="e.g. 120"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Locking mechanism
                    </label>
                    <select
                      value={form.safeLockingMechanism}
                      onChange={(e) =>
                        updateForm({ safeLockingMechanism: e.target.value })
                      }
                      className={inputClass}
                    >
                      <option value="">—</option>
                      {LOCKING_MECHANISMS.map((m) => (
                        <option key={m} value={m}>
                          {LOCKING_MECHANISM_LABELS[m]}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Locking bolts
                    </label>
                    <input
                      type="number"
                      min={0}
                      value={form.safeLockingBoltCount}
                      onChange={(e) =>
                        updateForm({ safeLockingBoltCount: e.target.value })
                      }
                      placeholder="e.g. 4"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Wall thickness (mm)
                    </label>
                    <input
                      type="number"
                      min={0}
                      value={form.safeWallThicknessMm}
                      onChange={(e) =>
                        updateForm({ safeWallThicknessMm: e.target.value })
                      }
                      placeholder="e.g. 6"
                      className={inputClass}
                    />
                  </div>
                  {/* Tri-state, not a checkbox: `hasAnchorPoints` is
                      `boolean | null` and it IS AI-enriched, so "unknown" has
                      to stay distinguishable from "no" or a fill-empty run
                      would overwrite a deliberate No. */}
                  {triSelect(
                    'Anchor points',
                    form.safeHasAnchorPoints,
                    (v) => updateForm({ safeHasAnchorPoints: v }),
                    'Ships pre-drilled for floor or wall anchoring.',
                  )}
                </div>
              </div>
            )}
          </section>

          {/* Display case — the third capability's own section.
              Gated on the BOX, not on the derived primary kind, exactly as the
              security panel is: a glass-fronted winder has real glazing and a
              real UV answer, and hiding them because its primary kind is
              `winder` would withhold true facts about the object.
              `resolveDisplayCaseSpecsWrite` clears the whole block when the box
              is unticked, so hiding the panel is presentation — the data cannot
              be left stranded behind it. */}
          <section className="space-y-3">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Display
            </h4>
            {caps.displayCase ? (
              <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                <p className="text-xs text-gray-500">
                  Only fill what you can verify — leave the rest blank. Blank
                  fields are stored as absent.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Glazing
                    </label>
                    <select
                      value={form.displayGlazing}
                      onChange={(e) =>
                        updateForm({ displayGlazing: e.target.value })
                      }
                      className={inputClass}
                    >
                      <option value="">Unknown</option>
                      {GLAZING_MATERIALS.map((m) => (
                        <option key={m} value={m}>
                          {GLAZING_LABELS[m]}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Mounting style
                    </label>
                    <select
                      value={form.displayMountingStyle}
                      onChange={(e) =>
                        updateForm({ displayMountingStyle: e.target.value })
                      }
                      className={inputClass}
                    >
                      <option value="">Unknown</option>
                      {MOUNTING_STYLES.map((m) => (
                        <option key={m} value={m}>
                          {MOUNTING_STYLE_LABELS[m]}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {triSelect(
                    'UV filtering',
                    form.displayHasUvFiltering,
                    (v) => updateForm({ displayHasUvFiltering: v }),
                    'The glazing filters UV — what decides whether dials and straps fade.',
                  )}
                  {triSelect(
                    'Illumination',
                    form.displayHasIllumination,
                    (v) => updateForm({ displayHasIllumination: v }),
                    'Integrated lighting — LED strips, fibre optics, a lit vitrine.',
                  )}
                  {/* These two are constantly confused in marketing copy, so
                      the hints name the difference rather than the feature. */}
                  {triSelect(
                    'Hygrometer',
                    form.displayHasHygrometer,
                    (v) => updateForm({ displayHasHygrometer: v }),
                    'MEASURES humidity only — a gauge, not a regulator.',
                  )}
                  {triSelect(
                    'Humidity control',
                    form.displayHasHumidityControl,
                    (v) => updateForm({ displayHasHumidityControl: v }),
                    'REGULATES humidity — a humidor compartment or a conditioned interior.',
                  )}
                  {triSelect(
                    'Drawer',
                    form.displayHasDrawer,
                    (v) => updateForm({ displayHasDrawer: v }),
                    'Closed storage below or beside the display bed.',
                  )}
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-600">
                Not a display case.{' '}
                <span className="text-gray-400">
                  Tick <strong>Is a display case</strong> at the top of this form
                  to record glazing, UV filtering, illumination and humidity. A
                  winder or a safe can be one too; the three are independent.
                </span>
              </p>
            )}
          </section>

          {/* Description */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Description
              </h4>
              <button
                type="button"
                onClick={() => setShowAiPanel((v) => !v)}
                disabled={!identityReady}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700 hover:bg-purple-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title={
                  identityReady
                    ? 'Identify and fill in specs using AI'
                    : 'Enter a make and model first — they are what the AI identifies the winder from'
                }
              >
                {showAiPanel ? '✕ Hide AI' : '✨ Suggest with AI'}
              </button>
            </div>
            <textarea
              value={form.description}
              onChange={(e) => updateForm({ description: e.target.value })}
              rows={5}
              className={inputClass}
              placeholder="What this winder is and where it sits in the range…"
            />
            {showAiPanel && (
              <div className="border border-purple-200 bg-purple-50 rounded-lg p-3">
                <div className="text-[10px] text-amber-700 bg-amber-50 px-2 py-1 rounded mb-3">
                  ⚠️ AI suggestions may contain errors — verify before applying.
                </div>
                <AiWinderEnrichPanel
                  state={aiState}
                  context={aiContext}
                  onContextChange={setAiContext}
                  runDisabled={!identityReady}
                  disabledReason="Enter a make and model first"
                  onRun={() => void runAiEnrich()}
                  onAccept={openAiApplyReview}
                  onClose={() => {
                    setShowAiPanel(false);
                    setAiState({});
                  }}
                />
              </div>
            )}
          </section>

          {/* Sources & status */}
          <section className="space-y-3">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Sources &amp; status
            </h4>
            <TagInput
              label="Source URLs"
              values={form.sourceUrls}
              onChange={(v) => updateForm({ sourceUrls: v })}
              placeholder="https://…"
            />
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.isDeprecated}
                onChange={(e) => updateForm({ isDeprecated: e.target.checked })}
                className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
              />
              <span>
                Deprecated{' '}
                <span className="text-gray-400">
                  — hidden from pickers, but existing user winders keep working
                </span>
              </span>
            </label>
          </section>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t">
            {canDelete && (
              <Button
                variant="ghost"
                onClick={handleDelete}
                disabled={saving}
                className="mr-auto text-red-600 hover:text-red-700"
              >
                Delete
              </Button>
            )}
            <Button variant="ghost" onClick={onClose} disabled={saving}>
              {isSavedView ? 'Close' : 'Cancel'}
            </Button>
            <Button variant="primary" onClick={handleSave} loading={saving}>
              {isSavedView ? 'Save changes' : `Create ${primaryLabel.toLowerCase()}`}
            </Button>
          </div>
        </div>
      </Modal>

      {/* AI apply-review diff */}
      <Modal
        isOpen={aiApplyOpen}
        onClose={() => setAiApplyOpen(false)}
        title="Apply AI suggestions"
        size="lg"
      >
        <div className="space-y-3">
          <p className="text-sm text-gray-600">
            Tick the fields to apply, then confirm. Applying overwrites the current
            value in the form — nothing is written until you save. Per-field
            confidence is shown in the AI panel above.
          </p>
          <div className="flex items-center gap-3 text-xs">
            <button
              type="button"
              onClick={() =>
                setAiFieldSel(Object.fromEntries(aiFieldDiffs.map((d) => [d.key, true])))
              }
              className="text-brand-600 hover:text-brand-800 underline"
            >
              Select all
            </button>
            <button
              type="button"
              onClick={() =>
                setAiFieldSel(Object.fromEntries(aiFieldDiffs.map((d) => [d.key, false])))
              }
              className="text-gray-500 hover:text-gray-700 underline"
            >
              Select none
            </button>
            <button
              type="button"
              onClick={() =>
                setAiFieldSel(
                  Object.fromEntries(aiFieldDiffs.map((d) => [d.key, d.changed])),
                )
              }
              className="text-gray-500 hover:text-gray-700 underline"
            >
              Changed only
            </button>
          </div>
          <div className="border border-gray-200 rounded-lg overflow-hidden max-h-[26rem] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-50 text-gray-500">
                <tr>
                  <th className="w-10 px-3 py-2" />
                  <th className="text-left font-medium px-3 py-2">Field</th>
                  <th className="text-left font-medium px-3 py-2">Current</th>
                  <th className="text-left font-medium px-3 py-2">AI proposed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {aiFieldDiffs.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-3 py-4 text-center text-gray-400">
                      No suggestions returned.
                    </td>
                  </tr>
                ) : (
                  aiFieldDiffs.map((d) => (
                    <tr key={d.key} className={d.changed ? undefined : 'opacity-60'}>
                      <td className="px-3 py-2 align-top">
                        <input
                          type="checkbox"
                          checked={!!aiFieldSel[d.key]}
                          onChange={(e) =>
                            setAiFieldSel((prev) => ({
                              ...prev,
                              [d.key]: e.target.checked,
                            }))
                          }
                          className="rounded border-gray-300 text-brand-600 focus:ring-brand-500 mt-0.5"
                        />
                      </td>
                      <td className="px-3 py-2 align-top text-gray-800">
                        {d.label}
                        {!d.changed && (
                          <span className="ml-1 text-[10px] text-gray-400">(same)</span>
                        )}
                      </td>
                      <td className="px-3 py-2 align-top font-mono text-gray-500 break-words max-w-[14rem]">
                        {d.current || '—'}
                      </td>
                      <td
                        className={`px-3 py-2 align-top font-mono break-words max-w-[14rem] ${
                          d.changed ? 'text-emerald-700' : 'text-gray-500'
                        }`}
                      >
                        {d.proposed}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="ghost" size="sm" onClick={() => setAiApplyOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => applyAiFields(new Set(aiFieldDiffs.map((d) => d.key)))}
            >
              Apply all
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={aiSelectedCount === 0}
              onClick={() =>
                applyAiFields(
                  new Set(
                    aiFieldDiffs.filter((d) => aiFieldSel[d.key]).map((d) => d.key),
                  ),
                )
              }
            >
              Apply selected ({aiSelectedCount})
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
